import React, { useState } from "react";
import {
    getPath,
    getAllPages,
} from "../../pages/ClaimAnalysisPage/ClaimViewPage/PatientDetails";
import Period from "./Period";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress } from "@mui/material";
import { useData } from "@pure-ptr/react";
import { fetchResources } from "../../store/api/fhir-api";
import Observations from "./Observation";
import ConditionList from "./ConditionList";
import ResourceList from "./ResourceList";

interface EncounterDetailProps {
    encounter: any;
    onBack: () => void;
    patientId: string;
    onSelectionChange?: (row: any, isChecked: boolean) => void;
    selectedResources?: Record<string, boolean>;
}

export const EncounterDetail = ({
    encounter,
    onBack,
    patientId,
    onSelectionChange,
    selectedResources = {},
}: EncounterDetailProps) => {
    const [groups, setGroups] = useState<any>({});
    const RESOURCE_TYPES = ["Observation", "Condition", "DiagnosticReport", "DocumentReference"];

    const getResourceKey = (resource: { id?: string; resourceType?: string }) => {
        if (!resource || !resource.id) {
            return "";
        }
        const type = resource.resourceType || "";
        return `${type}:${resource.id}`;
    };

    const syncSelectionInLocalGroups = (currentGroups: any, resourceId: string, isChecked: boolean) => {
        const updatedGroups = { ...currentGroups };

        Object.keys(updatedGroups).forEach(type => {
            const items = updatedGroups[type];
            if (!Array.isArray(items)) {
                return;
            }

            items.forEach((item: any) => {
                const resource = item.resource ?? item;
                if (resource && resource.id === resourceId) {
                    resource.isSelected = isChecked;
                }
            });
        });

        return updatedGroups;
    };

    const handleLocalSelectionChange = (row: any, isChecked: boolean) => {
        if (!row || !row.id) {
            return;
        }

        setGroups(prev => syncSelectionInLocalGroups(prev, row.id, isChecked));

        if (onSelectionChange) {
            onSelectionChange(row, isChecked);
        }
    };

    const { isPending } = useData(async () => {
        if (!encounter?.id) return null;

        const tempGroups: any = {};
        RESOURCE_TYPES.forEach((type) => {
            tempGroups[type] = [];
        });

        const addToGroups = (entries, type) => {
            if (!entries?.length) return;
            if (!Array.isArray(tempGroups[type])) {
                tempGroups[type] = [];
            }
            entries.forEach((entry) => {
                const resource = entry.resource ?? entry;
                if (resource) {
                    const key = getResourceKey(resource);
                    if (key && selectedResources[key]) {
                        resource.isSelected = true;
                    }
                }
                tempGroups[type].push(entry);
            });
        };

        const observations = await fetchResources(
            "/api/v1/Observation/",
            patientId,
            encounter.id,
            null,
        );
        if (observations) {
            const processed = await getAllPages(
                observations,
                "/api/v1/Observation/",
                patientId,
                encounter.id,
            );
            addToGroups(processed, "Observation");
        }

        const conditions = await fetchResources(
            "/api/v1/Condition/",
            patientId,
            encounter.id,
            null,
        );
        if (conditions) {
            const processed = await getAllPages(
                conditions,
                "/api/v1/Condition/",
                patientId,
                encounter.id,
            );
            addToGroups(processed, "Condition");
        }

        const diagnosticReports = await fetchResources(
            "/api/v1/DiagnosticReport/",
            patientId,
            encounter.id,
            null,
        );
        if (diagnosticReports) {
            const processed = await getAllPages(
                diagnosticReports,
                "/api/v1/DiagnosticReport/",
                patientId,
                encounter.id,
            );
            addToGroups(processed, "DiagnosticReport");
        }

        const documentReferences = await fetchResources(
            "/api/v1/DocumentReference/",
            patientId,
            encounter.id,
            null,
        );
        if (documentReferences) {
            const processed = await getAllPages(
                documentReferences,
                "/api/v1/DocumentReference/",
                patientId,
                encounter.id,
            );
            addToGroups(processed, "DocumentReference");
        }

        setGroups(tempGroups);
    }, [encounter?.id, patientId]);

    const renderResources = (type) => {
        const items = groups[type] || [];

        const commonProps = {
            resources: items,
            onSelectionChange: (row, checked) => handleLocalSelectionChange(row, checked),
        };

        if (type.indexOf("Observation") === 0) {
            return <Observations {...commonProps} />;
        }

        switch (type) {
            case "Condition":
                return <ConditionList {...commonProps} />;
            default:
                return <ResourceList {...commonProps} type={type} />;
        }
    };

    if (!encounter) return null;

    return (
        <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
            <div
                className="panel panel-default"
                style={{ margin: "0 0 0 0", flexShrink: 0 }}
            >
                <div
                    className="panel-heading"
                    style={{ display: "flex", alignItems: "center" }}
                >
                    <IconButton
                        size="small"
                        onClick={onBack}
                        style={{ marginRight: 8 }}
                    >
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <b className="text-primary">Encounter Details</b>
                </div>
                <div className="panel-body" style={{ padding: "10px 15px" }}>
                    <div className="row">
                        <div className="col-xs-4 col-sm-2 col-lg-1 text-right text-muted">
                            Type:
                        </div>
                        <div className="col-xs-8 col-sm-3 col-lg-2 text-left">
                            {getPath(encounter, "type.0.text") || "Unknown"}
                        </div>

                        <div className="col-xs-4 col-sm-2 col-lg-1 text-right text-muted">
                            Status:
                        </div>
                        <div className="col-xs-8 col-sm-3 col-lg-2 text-left">
                            {encounter.status || "Unknown"}
                        </div>

                        <div className="col-xs-4 col-sm-2 col-lg-1 text-right text-muted">
                            Period:
                        </div>
                        <div className="col-xs-8 col-sm-3 col-lg-2 text-left">
                            {encounter.period
                                ? Period(encounter.period)
                                : "Unknown"}
                        </div>

                        <div className="col-xs-4 col-sm-2 col-lg-1 text-right text-muted">
                            Reason:
                        </div>
                        <div className="col-xs-8 col-sm-3 col-lg-2 text-left">
                            {getPath(encounter, "reason.0.coding.0.display") ||
                                "Unknown"}
                        </div>

                        {/* <div className="col-xs-4 col-sm-2 col-lg-1 text-right text-muted">
                            Participant:
                        </div>
                        <div className="col-xs-8 col-sm-3 col-lg-2 text-left">
                            {getPath(
                                encounter,
                                "participant.0.individual.display",
                            ) || "Unknown"}
                        </div> */}

                        {/* <div className="col-xs-4 col-sm-2 col-lg-1 text-right text-muted">
                            Provider:
                        </div>
                        <div className="col-xs-8 col-sm-3 col-lg-2 text-left">
                            {getPath(encounter, "serviceProvider.display") ||
                                "Unknown"}
                        </div> */}
                    </div>
                </div>
            </div>

            {/* Resources - scrolls independently */}
            <div style={{ flex: 1, overflowY: "auto", paddingTop: 16 }}>
                {isPending ? (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: 20,
                        }}
                    >
                        <CircularProgress
                            size={35}
                            thickness={2}
                            color="secondary"
                        />
                    </div>
                ) : (
                    <div>
                        {RESOURCE_TYPES.map((type) => (
                            <div key={type} style={{ marginBottom: 20 }}>
                                {renderResources(type)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EncounterDetail;
